import { useMemo } from 'react';

import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import PanelActions from '../../page-framework/panel/PanelActions';
import {
    PANEL_CATEGORIES,
    AREA_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { useId } from '../../page-framework/panel/Panel';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import ChartTypeForm from '../../charts/ipm/ChartTypeForm';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { serverFilterId, timeRangeFilterId } from '../../filters';
import useChartDisplayType from '../../../utilities/UseChartDisplayType';

const title = t`Server Disk I/O`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'server',
        metrics: ['avgDiskOutRate', 'avgDiskInRate', 'avgDiskOut', 'avgDiskIn'],
    },
};

const RATE = 'rate';
const ACTIVE_TIME = 'activeTime';

function getChartSeries(type, key, label) {
    return type === RATE
        ? {
              formatter: FORMATTER_TYPES.LOG,
              units: ` ${t`Bytes/s`}`,
              key: `${key}Rate`,
              label,
              multiplier: 1,
          }
        : {
              formatter: FORMATTER_TYPES.PERCENT,
              units: '%',
              forceDecimals: true,
              key,
              label,
              multiplier: 1,
          };
}

function useChartConfig(type) {
    const readColor = useMetricColor('serverDiskOut');
    const writeColor = useMetricColor('serverDiskIn');

    return useMemo(
        () => ({
            yAxis: [
                {
                    title: {
                        text: type === RATE ? t`IO (Bytes/s)` : t`Active Time (%)`,
                    },
                    ceiling: type === RATE ? undefined : 200,
                },
            ],
            chartType: 'areaspline',
            stacking: 'normal',
            tooltip: BuiltInTooltip,
            showGranularity: true,
            chartTrends: [
                {
                    ...getChartSeries(type, 'avgDiskIn', t`Write`),
                    color: writeColor,
                },
                {
                    ...getChartSeries(type, 'avgDiskOut', t`Read`),
                    color: readColor,
                },
            ],
        }),
        [readColor, writeColor, type]
    );
}

const selections = [
    { value: RATE, label: t`Rate` },
    { value: ACTIVE_TIME, label: t`Active Time` },
];

function ServerDiskIOPanel() {
    const id = useId();
    const pageStateKey = `${id}-ChartType`;
    const [chartDisplayType, setChartDisplayType] = useChartDisplayType(pageStateKey, RATE);
    const chartConfig = useChartConfig(chartDisplayType);
    const data = usePanelDataService(dataSource);
    const chartData = data?.data?.[0];

    if (!data) return null;

    return (
        <>
            <PanelActions>
                <ChartTypeForm value={chartDisplayType} onChangeType={setChartDisplayType} selections={selections} />
            </PanelActions>
            <PerformanceChart chartDef={chartConfig} data={chartData} />
        </>
    );
}

ServerDiskIOPanel.propTypes = panelPropTypes;

ServerDiskIOPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'ServerDiskIOTrend',
    catalogEntry: {
        category: PANEL_CATEGORIES.SERVER,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Disk I/O trend for a selected server`,
        previewImage: AREA_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, serverFilterId],
        optional: [],
    },
})(ServerDiskIOPanel);
